import {
  Component,
  EventEmitter,
  forwardRef,
  OnInit,
  OnDestroy,
  Input,
  Output,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { ButtonSize, ButtonStyle } from '@constants/button/1.0';
import { timeAfterValidator, timeBeforeValidator } from '@helpers/Validators';
import { WeekHours } from '@generated/tcea/models';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { HoursUtilitiesService } from '@services/hours/1.0/hours-utilities.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DAYS_OF_WEEK_SHORT } from '@constants/datetime/1.0';
import { DeepCloneService } from '@services/utilities/1.0/deep-clone.service';
import { NgIf, NgFor } from '@angular/common';
import { ButtonComponent } from '../../button/1.0/button.component';
import { FormSubmittedSyncDirective } from '../../../directives/form-submitted-sync/1.0/form-submitted-sync.directive';
import { MatFormField, MatLabel, MatError, MatHint } from '@angular/material/form-field';
import { MatSelect } from '@angular/material/select';
import { SelectCheckAllComponent } from '../../select-check-all/select-check-all.component';
import { MatOption } from '@angular/material/core';
import { MatInput } from '@angular/material/input';
import { MatButton } from '@angular/material/button';
@Component({
  selector: 'txp-store-hours-template-form',
  templateUrl: './store-hours-template-form.component.html',
  styleUrls: ['./store-hours-template-form.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => StoreHoursTemplateFormComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => StoreHoursTemplateFormComponent),
      multi: true,
    },
  ],
  standalone: true,
  imports: [
    NgIf,
    ButtonComponent,
    FormSubmittedSyncDirective,
    FormsModule,
    ReactiveFormsModule,
    NgFor,
    MatFormField,
    MatLabel,
    MatSelect,
    SelectCheckAllComponent,
    MatOption,
    MatError,
    MatInput,
    MatHint,
    MatButton,
    TranslateModule,
  ],
})
export class StoreHoursTemplateFormComponent
  implements ControlValueAccessor, Validator, OnDestroy, OnInit
{
  @Input() defaultRowNotRequired: boolean;
  @Input() selectAllDaysByDefault = false;
  @Output() deleteStoreHours: EventEmitter<any> = new EventEmitter();

  buttonStyle = ButtonStyle; // expose enum to template
  buttonSize = ButtonSize;
  form: UntypedFormGroup;
  daysOfWeek = DAYS_OF_WEEK_SHORT;
  onTouched: any;
  disabled: boolean;
  onChangeFn: any;
  allSelected = false;
  private TIME_REGEX = HoursUtilitiesService.get24HourTimeRegex();
  private unsubscribe = new Subject<void>();

  constructor(
    private formBuilder: UntypedFormBuilder,
    private translateService: TranslateService,
  ) {}

  ngOnInit(): void {
    if (this.defaultRowNotRequired) {
      this.form = this.formBuilder.group({
        items: new UntypedFormArray([]),
      });
    } else {
      this.form = this.formBuilder.group({
        items: new UntypedFormArray([this.createListItem()]),
      });
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  writeValue(obj: WeekHours): void {
    // by default, have 1 array value
    if (!this.defaultRowNotRequired) {
      let formValue: any[] = [
        {
          days: this.selectAllDaysByDefault ? [...this.daysOfWeek] : '',
          endTime: '',
          startTime: '',
        },
      ];

      if (obj) {
        formValue = HoursUtilitiesService.convertHoursToForm(DeepCloneService.clone(obj));
      }
      const formArray = this.form.get('items') as UntypedFormArray;

      // clear array
      formArray.clear();

      const controls: UntypedFormGroup[] = [];
      for (const value of formValue) {
        controls.push(this.createListItem(value));
      }
      // set new FormArray all at once to avoid unnecessary change events
      this.form.setControl('items', new UntypedFormArray(controls));
    } else {
      const formArray = this.form.get('items') as UntypedFormArray;

      // clear array
      formArray.clear();

      // add form controls for each item
      const controls: UntypedFormGroup[] = [];
      if (obj) {
        const formValue = HoursUtilitiesService.convertHoursToForm(DeepCloneService.clone(obj));
        for (const value of formValue) {
          controls.push(this.createListItem(value));
        }
      }

      // set new FormArray all at once to avoid unnecessary change events
      this.form.setControl('items', new UntypedFormArray(controls));
    }
  }

  registerOnChange(fn: any): void {
    this.onChangeFn = fn;
    this.form.valueChanges.pipe(takeUntil(this.unsubscribe)).subscribe((data) => {
      // convert data to Days
      const storeHours = HoursUtilitiesService.convertFormToStoreHours(data.items);

      fn(storeHours);
    });
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
    isDisabled ? this.form.disable() : this.form.enable();
  }

  validate(c: AbstractControl): ValidationErrors | null {
    return this.form.valid
      ? undefined
      : {
          invalidForm: {
            valid: false,
            message: this.translateService.instant('form.error.fieldsAreInvalid'),
          },
        };
  }

  /**
   * Creates a form instance of store hours item
   */
  createListItem(defaults?) {
    let days: string | string[] = this.selectAllDaysByDefault ? [...this.daysOfWeek] : '';
    let startTime = '';
    let endTime = '';
    if (defaults) {
      days = defaults.days;
      startTime = defaults.startTime;
      endTime = defaults.endTime;
    }
    const group = this.formBuilder.group({
      days: new UntypedFormControl(days, Validators.required),
      startTime: new UntypedFormControl(startTime, [
        Validators.required,
        Validators.pattern(this.TIME_REGEX),
      ]),
      endTime: new UntypedFormControl(endTime, [
        Validators.required,
        Validators.pattern(this.TIME_REGEX),
      ]),
    });
    // Add validators that depend on other controls
    const startTimeField = group.get('startTime');
    const endTimeField = group.get('endTime');

    // Need to make sure to include current validator function too
    startTimeField.setValidators([startTimeField.validator, timeBeforeValidator(endTimeField)]);
    endTimeField.setValidators([endTimeField.validator, timeAfterValidator(startTimeField)]);

    group.valueChanges.subscribe(() => {
      startTimeField.updateValueAndValidity({ emitEvent: false });
      endTimeField.updateValueAndValidity({ emitEvent: false });
    });

    return group;
  }

  /**
   * Adds a new StoreHoursItemModel to hour template list
   */
  addListItem() {
    const items = this.form.get('items') as UntypedFormArray;
    items.insert(0, this.createListItem());
  }

  /**
   * Removes clicked row item from list
   */
  removeListItem(index) {
    const items = this.form.get('items') as UntypedFormArray;
    items.removeAt(index);
    this.deleteStoreHours.emit(true);
  }
}
